// ============================================================================
// ASSIGNMENTS TABLE — LifeOS School.dc.html, option 6b.
//
// Five columns on a fixed grid (1fr 118 132 96 74), an overdue row on a danger ground, and a
// SCORE column whose empty cell is an em dash rather than blank space — because a blank cell in a
// table of numbers reads as zero.
//
// STATUS IS CANVAS'S WORD, TRANSLATED ONCE. Canvas says `missing`, which describes the
// submission; the owner's question is about the deadline, so the pill says 'overdue'. Everything
// else keeps Canvas's own word.
//
// EVERY ROW IS A BUTTON (2026-08-29). It was a `<div>`, which is why the owner's report was that
// the table "does nothing" — a five-column read-out with a hover tint and no gesture behind it.
// Clicking a row opens the detail peek (./AssignmentPeek) beside it: the description Canvas
// carries, the lock date, the grading type, how it is handed in, and a link out to bCourses.
// The GRID is unchanged, so the button renders `display: grid` and the five columns still line
// up with the header row above them.
// ============================================================================

import type { CSSProperties } from 'react'
import type { Assignment, AssignmentStatus, Course } from '@app/shared'

import { SkeletonRows } from '../Card'
import { ValueSwap } from '../motion/ValueSwap'
import { DocText } from './icons'
import { AssignmentPeek, useAssignmentPeek } from './AssignmentPeek'
import { assignmentKindLabel, assignmentStatusLabel, dueCellLabel, hueVar } from './derive'
import { Unavailable } from './Chrome'

const GRID = 'grid grid-cols-[1fr_118px_132px_96px_74px] items-center gap-3'

const STATUS_PILL: Record<AssignmentStatus, string> = {
  missing: 'bg-danger-line-2 text-danger-2',
  upcoming: 'bg-surface-4 text-muted',
  submitted: 'bg-info-fill text-info',
  graded: 'bg-success-fill text-success',
}

export function AssignmentsTable({
  assignments,
  course,
  isPending,
  error,
}: {
  assignments: Assignment[] | undefined
  course: Course | null | undefined
  isPending: boolean
  error: unknown
}) {
  // WHAT STILL NEEDS DOING COMES FIRST. The API answers in due-date order, which is the right
  // order for a runway and the wrong one for a table you open to decide what to work on: it
  // buries an overdue essay under three things you already handed in. So the table re-sorts —
  // overdue, then upcoming by date, then everything closed, newest first — and re-sorting is the
  // table's business rather than the API's, because the same list feeds the runway unchanged.
  const rows = [...(assignments ?? [])].sort((a, b) => {
    const rank = (x: Assignment) => (x.status === 'missing' ? 0 : x.status === 'upcoming' ? 1 : 2)
    if (rank(a) !== rank(b)) return rank(a) - rank(b)
    const direction = rank(a) === 2 ? -1 : 1
    // UNDATED LAST, ALWAYS — and still present. Most Canvas assignments carry no due date, and
    // dropping them here would tell the owner a course has five items when it has thirteen.
    if (!a.dueAt || !b.dueAt) return a.dueAt ? -1 : b.dueAt ? 1 : 0
    return a.dueAt < b.dueAt ? -direction : a.dueAt > b.dueAt ? direction : 0
  })
  // OVERDUE MEANS STILL OPEN AND PAST ITS DATE. Counting "anything whose date has passed" would
  // make every graded assignment in the course overdue forever, which is how a counter that
  // should mean "act on this" turns into a number nobody reads.
  const overdue = rows.filter((a) => a.status === 'missing').length
  const peek = useAssignmentPeek()

  return (
    <section className="flex flex-none flex-col gap-[9px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <DocText size={16} strokeWidth={1.7} className="flex-none text-muted" />
        <h2 className="text-[13px] font-semibold leading-none">Assignments</h2>
        {assignments && (
          <span className="text-[11px] font-normal leading-none text-microcopy">
            {rows.length} {rows.length === 1 ? 'item' : 'items'}
            {overdue > 0 && ` · ${overdue} overdue`}
          </span>
        )}
        <button
          type="button"
          disabled
          title="Canvas has no assignment-type field, so there is nothing to filter by — which is why the TYPE column is mostly a dash. LifeOS will not guess a taxonomy that does not exist."
          className="ml-auto cursor-not-allowed text-[11px] font-medium leading-none text-muted"
        >
          All types
        </button>
      </header>

      {isPending && <SkeletonRows rows={5} />}
      <Unavailable error={error} />
      {assignments && rows.length === 0 && (
        <p className="text-[12px] leading-[1.45] text-microcopy">
          Canvas has published no assignments for this course.
        </p>
      )}

      {assignments && rows.length > 0 && (
        <>
          <div className={`${GRID} px-[13px] pb-1`}>
            <Head>ASSIGNMENT</Head>
            <Head>TYPE</Head>
            <Head>DUE</Head>
            <Head>STATUS</Head>
            <Head className="text-right">SCORE</Head>
          </div>

          <div className="flex flex-col gap-[2px]">
            {rows.map((a) => {
              const isOverdue = a.status === 'missing'
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={(event) => peek.openFor(a, event.currentTarget)}
                  aria-expanded={peek.open?.assignment.id === a.id}
                  aria-haspopup="dialog"
                  style={
                    {
                      '--mo-rule': isOverdue
                        ? 'var(--color-danger-dot)'
                        : hueVar(course?.code ?? ''),
                    } as CSSProperties
                  }
                  className={[
                    GRID,
                    'mo-rule w-full rounded-tile px-[13px] py-2 text-left',
                    isOverdue ? 'bg-danger-fill' : 'bg-surface',
                  ].join(' ')}
                >
                  <span className="min-w-0 text-[12.5px] font-semibold leading-[1.25] text-pretty">
                    {a.title}
                  </span>
                  <span
                    className="text-[11.5px] font-normal leading-none text-muted"
                    title={
                      a.kind === 'unclassified'
                        ? 'Canvas has no assignment-type field, so LifeOS does not guess one.'
                        : undefined
                    }
                  >
                    {assignmentKindLabel(a.kind)}
                  </span>
                  <span
                    className={[
                      'text-[11.5px] font-medium leading-none',
                      isOverdue ? 'text-danger-2' : 'text-ink-2',
                    ].join(' ')}
                  >
                    {dueCellLabel(a, course)}
                  </span>
                  <span>
                    <span
                      className={[
                        'inline-block rounded-pill px-[9px] py-1 text-[10.5px] font-semibold leading-none',
                        STATUS_PILL[a.status],
                      ].join(' ')}
                    >
                      {assignmentStatusLabel(a)}
                    </span>
                  </span>
                  {/* Not blank. A blank cell in a column of numbers is read as a zero, and a
                      zero here would say "marked, and you got nothing". */}
                  {/* 13f · A GRADE LANDS. The score rises into place when it CHANGES — which,
                      on this table, is the moment a Canvas sync turns an em dash into a number.
                      A row Canvas re-sent unchanged does not move, and that stillness is 13c's
                      rule doing its job in the same column. */}
                  <ValueSwap
                    value={a.score === null ? '—' : `${a.score}/${a.pointsPossible}`}
                    className={[
                      'w-full text-right text-[12px] font-semibold leading-none',
                      a.score === null ? 'text-faintest-2' : 'text-ink',
                    ].join(' ')}
                  >
                    <span title={a.score === null ? 'Not marked yet — no score exists.' : undefined}>
                      {a.score === null ? '—' : `${a.score}/${a.pointsPossible}`}
                    </span>
                  </ValueSwap>
                </button>
              )
            })}
          </div>
        </>
      )}

      {peek.open && (
        <AssignmentPeek
          assignment={peek.open.assignment}
          course={course}
          anchor={peek.open.anchor}
          onClose={peek.close}
        />
      )}
    </section>
  )
}

function Head({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={[
        'text-[9.5px] font-semibold leading-none tracking-[0.08em] text-microcopy',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </span>
  )
}
